import {BackButton} from '@uwm/ui';
import {useSelector} from 'react-redux';

import InfoButton from '@/components/InfoButton/InfoButton';
import MetricBar from '@/components/MetricBar/MetricBar';
import TextItemList from '@/components/TextItemList/TextItemList';
import TipsToConsider from '@/components/TipsToConsider/TipsToConsider';
import {Text} from '@/components/Typography';
import experienceScoreStatuses from '@/consts/experienceScore/experienceScoreStatuses';
import {getContent} from '@/services/content/content';
import metricMapping from '@/services/experienceScore/mappings/metricMapping';
import tipsToConsiderMapping from '@/services/experienceScore/mappings/tipsToConsiderMapping';
import modalService from '@/services/modal/modalService';
import {selectExperienceInsightsAndTips} from '@/state/selectors/inference';
import type {TipEntry} from '@/types/inference';

import AboutSection from './AboutSection';

interface MetricDetailProps {
    code: string;
    titleId: string;
    onClose: () => void;
}

/** Metric page driven by the AEM metric detail - only the bar value comes from the inference. */
const MetricDetail = ({code, titleId, onClose}: MetricDetailProps) => {
    const insightsAndTips = useSelector(selectExperienceInsightsAndTips);
    const {title, explanation, detail} = metricMapping.getMetric(code) ?? {};
    // Back leads to the score page, so it takes that page's title - like an iOS back button.
    const backLabel = getContent().experienceScoreContent?.title;
    const entry = insightsAndTips?.[code] as TipEntry | undefined;
    const status = entry?.status;
    const isSubOptimal = status === experienceScoreStatuses.SUB_OPTIMAL;
    const tip = tipsToConsiderMapping.getMetricPageTip({insightId: code, insightsAndTips});

    return (
        <div className='flex min-h-dvh w-full flex-col px-4 pt-inset-4 text-primary dark:bg-orion-1 dark:text-romance'>
            <div className='flex items-center justify-between'>
                <BackButton label={backLabel} onClick={onClose} />
                {explanation && <InfoButton label={explanation.title} onClick={() => modalService.openInfo(code)} />}
            </div>
            <Text id={titleId} as='h1' variant='pageTitle' className='mt-4 text-left'>
                {title}
            </Text>
            <MetricBar
                className='mt-4'
                progressBar={explanation?.progressBar}
                value={entry?.current_behavior ?? 0}
                max={entry?.threshold}
                status={status}
                labelledBy={titleId}
            />
            {tip?.description && <TipsToConsider containerClassName='mt-4' tipsToConsiderData={[tip]} />}
            {isSubOptimal && (
                <TextItemList
                    className='mt-9'
                    title={detail?.mightExperienceTitle}
                    items={detail?.mightExperienceItems}
                />
            )}
            <AboutSection title={detail?.aboutTitle} description={explanation?.description} />
        </div>
    );
};

export default MetricDetail;
